import React, { type PropsWithChildren, type ReactNode, useMemo, useState } from "react";

import PortfolioSuperpositionsChartTooltip from "@/components/Portfolio/Superpositions/ChartTooltip";
import { SupHeading, SupDownTrend, SupUpTrend } from "@repo/core-ui";

import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import usePortfolioBalance from "@/hooks/portfolio/usePortfolioBalance";
import { formatCurrencyCompact } from "@/utils/formatters";
import { twMerge } from "tailwind-merge";

type TimeRange = "1w" | "1m" | "1y" | "max";

interface PortfolioSuperpositionsSummaryProps {
  label?: string;
  badgeChildren?: ReactNode;
  disabled?: boolean;
}

export default function PortfolioSuperpositionsSummary({ children, label = "Summary", badgeChildren }: PropsWithChildren<PortfolioSuperpositionsSummaryProps>) {
  const [timeRange, setTimeRange] = useState<TimeRange>("1w");
  const { data: chartData, isLoading } = usePortfolioBalance();

  const { filteredData, valueChange, percentageChange, yAxisDomain } = useMemo(() => {
    if (!chartData?.length) return { filteredData: [], valueChange: 0, percentageChange: 0, yAxisDomain: [0, 0] };

    const now = Date.now() / 1000;
    const ranges = {
      "1w": 7 * 24 * 60 * 60,
      "1m": 30 * 24 * 60 * 60,
      "1y": 365 * 24 * 60 * 60,
      max: Infinity,
    };

    // Sort data chronologically (oldest to newest)
    const sortedData = [...chartData].sort((a, b) => a.timestamp - b.timestamp);

    const filtered = sortedData.filter(point => point.timestamp >= now - ranges[timeRange]);
    if (filtered.length < 2) return { filteredData: filtered, valueChange: 0, percentageChange: 0, yAxisDomain: [0, 0] };

    const currentValue = Number(filtered[filtered.length - 1].balance_usd);
    const startValue = Number(filtered[0].balance_usd);
    const change = currentValue - startValue;
    const percentage = (change / startValue) * 100;

    // Calculate min and max for Y axis with 10% padding
    const values = filtered.map(d => Number(d.balance_usd));
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const padding = (maxValue - minValue) * 0.1;

    return {
      filteredData: filtered,
      valueChange: change,
      percentageChange: percentage,
      yAxisDomain: [minValue - padding, maxValue + padding],
    };
  }, [chartData, timeRange]);

  return (
    <section className="grid w-full grid-cols-1 gap-4 bg-neutral-25 p-4 md:grid-cols-2">
      <div className="space-y-1">
        <div className="flex w-full items-center justify-between text-neutral-900 md:w-auto md:justify-start">
          <SupHeading variant="h6" weight="bold">
            {label}
          </SupHeading>
        </div>
        {children}
      </div>

      <div className="flex h-full flex-col justify-between">
        <div className="flex flex-wrap items-center justify-between">
          <div className="mb-2 space-y-1">
            <SupHeading variant="h6" weight="bold">
              Value
            </SupHeading>
            <SupHeading
              variant="h6"
              weight="bold"
              className={twMerge(
                "flex items-center space-x-1 text-[14px] leading-4",
                percentageChange > 0 ? "text-success-600" : percentageChange < 0 ? "text-error-600" : "text-neutral-500"
              )}
            >
              <span
                className={twMerge(
                  "inline-flex px-1 py-0.5 text-[16px] leading-none",
                  percentageChange > 0 ? "bg-success-600 bg-opacity-10" : percentageChange < 0 ? "bg-error-600 bg-opacity-10" : "text-neutral-500"
                )}
              >
                {timeRange}
              </span>
              <span>{formatCurrencyCompact(valueChange)}</span>
              {percentageChange > 0 ? <SupUpTrend className="h-4 w-4" /> : percentageChange < 0 && <SupDownTrend className="h-4 w-4" />}
            </SupHeading>
          </div>
          {badgeChildren && <div className="-my-1  flex select-none flex-wrap items-center gap-2">{badgeChildren}</div>}
        </div>

        <div className="h-[100px] w-full">
          {isLoading ? (
            <div className="flex h-full items-center justify-center font-mono">Loading...</div>
          ) : (
            <div className="h-full w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={filteredData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                  <XAxis
                    dataKey="timestamp"
                    type="number"
                    scale="time"
                    domain={["dataMin", "dataMax"]}
                    hide
                    tickFormatter={value => {
                      const date = new Date(value * 1000);
                      return date.toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      });
                    }}
                  />
                  <YAxis domain={yAxisDomain} hide allowDataOverflow={false} />
                  <CartesianGrid horizontal={false} vertical stroke={"#8979D9"} strokeOpacity={0.1} />
                  <Tooltip content={<PortfolioSuperpositionsChartTooltip />} />
                  <Line type="monotone" dataKey="balance_usd" stroke="#8979D9" strokeWidth={1} dot={false} connectNulls={true} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
        <div className="flex justify-center space-x-2">
          {(["1w", "1m", "1y", "max"] as const).map(range => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={twMerge(
                "px-2 py-2 font-mono text-sm uppercase",
                timeRange === range ? "bg-primary-800 text-neutral-25" : "bg-transparent text-neutral-900 hover:bg-neutral-200"
              )}
            >
              {range}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
